import {
  filterToRenderableDocuments,
  getDocumentReferencesByIdViaAliases,
} from '@ctw/shared/api/fhir/document';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import type { FqsRequestOptions } from '@ctw/shared/api/fqs/client';

import { searchProvenancesFQS } from '@ctw/shared/api/fqs/queries/provenances';
import type { CTWState } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import {
  estimatedDecodedSizeOfBase64InBytes,
  maximumRenderableCdaDocumentSizeInBytes,
} from '@ctw/shared/utils/cda/document-size-limits';
import {
  FQS_HOTSTORAGE_REQUEST_OPTIONS,
  HOTSTORAGE_REQUEST_HEADERS,
} from '@ctw/shared/utils/hotstorage-header';
import {
  QUERY_KEY_BINARY,
  QUERY_KEY_BINARY_ID,
  QUERY_KEY_CCDA_PATIENT,
  QUERY_KEY_DOCUMENT_REFERENCE_FOR_BINARY_ID,
  QUERY_KEY_DOCUMENT_REFERENCES_FOR_BINARY_IDS,
  QUERY_KEY_PARENT_DOCUMENTS,
} from '@ctw/shared/utils/query-keys';
import type { Binary, Provenance, Resource } from 'fhir/r4';
import type { GraphQLClient } from 'graphql-request';
import { compact, find, some, uniq } from 'lodash-es';
import type { FHIRModel } from './models/fhir-model';
import { getFirstPartyPatientByIdentifier } from './patient-helper';
import { searchProvenances } from './provenance';
import { SYSTEM_ZUS_UNIVERSAL_ID } from './system-urls';

// Get the binary ID from provenance, if document is renderable
export function getBinaryId(provenances: Array<Provenance>, targetId: string): string | undefined {
  for (const provenance of provenances) {
    const hasTarget = some(provenance.target, (t) => t.reference?.includes(targetId));

    if (hasTarget) {
      const source = find(provenance.entity, { role: 'source' });
      if (source?.what.reference) {
        // Return the ID portion of the reference.
        return source.what.reference.split('/')[1];
      }
    }
  }

  return undefined;
}

// The resource ids whose provenance points at a model's source document.
// Summary/lens resources have no provenance of their own, so we follow their
// `aggregatedFromIds` to the underlying source resources that do.
export function getProvenanceTargetIds<T extends Resource>(model: FHIRModel<T>): Array<string> {
  return model.isSummaryResource ? model.aggregatedFromIds : [model.id];
}

// Given provenances covering a model's source resource(s), returns the first
// source-document Binary id. Short-circuits when the model carries its source
// Binary directly via the DA meta extension (no provenance lookup needed);
// otherwise follows summary/lens resources to their underlying
// source resources via `getProvenanceTargetIds`.
export function getSourceBinaryId<T extends Resource>(
  provenances: Array<Provenance>,
  model: FHIRModel<T>,
): string | undefined {
  if (model.sourceBinaryId) {
    return model.sourceBinaryId;
  }
  for (const targetId of getProvenanceTargetIds(model)) {
    const binaryId = getBinaryId(provenances, targetId);
    if (binaryId) {
      return binaryId;
    }
  }
  return undefined;
}

// We optionally look for any associated binary CCDAs
// if getSourceDocument is true.
export function useBinaryId<T extends Resource, M extends FHIRModel<T>>(
  model: M | undefined,
  getSourceDocument?: boolean,
  shouldFetchBinaryDocument?: boolean,
) {
  return useCtwQuery({
    queryId: QUERY_KEY_BINARY_ID,
    queryKey: [model?.id, getSourceDocument],
    queryFn: async ({ graphqlClient, telemetry }) => {
      if (model instanceof DocumentModel) {
        return model.binaryId;
      }

      if (!model) {
        return null;
      }

      if (model instanceof DocumentModel) {
        // Special handling for document models which already have a binaryID.
        return model.binaryId ?? null;
      }
      if (getSourceDocument && !model.isSummaryResource) {
        const provenances = await searchProvenances(telemetry, graphqlClient, [model]);
        return getBinaryId(provenances, model.id) ?? null;
      }

      return null;
    },
    enabled: shouldFetchBinaryDocument,
  });
}

interface UseParentDocumentsArgs<T extends Resource> {
  upid: string;
  models: Array<FHIRModel<T>>;
  enabled?: boolean;
}

/**
 * Maps each model to the source document it came from (model -> Provenance ->
 * source Binary -> that Binary's DocumentReference), so a section-level
 * DocumentReference resolves to its parent clinical document. This is the
 * batched form of what `useOpenSourceDocument` does on click. Models with no
 * source document are absent from the map.
 *
 * All `models` must share a resource type; the Provenance search is per-type.
 * Group mixed resources first, as `useClientResolvedCitations` does.
 */
export function useParentDocuments<T extends Resource>({
  upid,
  models,
  enabled = true,
}: UseParentDocumentsArgs<T>) {
  // Sorted so the react-query key is stable regardless of input order.
  const modelIds = models.map((model) => model.id).sort((a, b) => a.localeCompare(b));

  return useCtwQuery({
    queryId: QUERY_KEY_PARENT_DOCUMENTS,
    queryKey: [upid, modelIds],
    queryFn: async ({ telemetry, graphqlClient }) => {
      const parentByModelId = new Map<string, DocumentModel>();
      if (models.length === 0) {
        return parentByModelId;
      }

      const provenances = await searchProvenances(telemetry, graphqlClient, models);
      const binaryIdByModelId = new Map<string, string>();
      for (const model of models) {
        const binaryId = getSourceBinaryId(provenances, model);
        if (binaryId) {
          binaryIdByModelId.set(model.id, binaryId);
        }
      }
      if (binaryIdByModelId.size === 0) {
        return parentByModelId;
      }

      const documents = await getDocumentReferencesForBinaryIds({
        binaryIds: [...new Set(binaryIdByModelId.values())],
        graphqlClient,
        telemetry,
        options: FQS_HOTSTORAGE_REQUEST_OPTIONS,
      });
      const documentByBinaryId = new Map<string, DocumentModel>();
      for (const document of documents) {
        if (document.binaryId) {
          documentByBinaryId.set(document.binaryId, document);
        }
      }
      for (const [modelId, binaryId] of binaryIdByModelId) {
        const document = documentByBinaryId.get(binaryId);
        if (document) {
          parentByModelId.set(modelId, document);
        }
      }
      return parentByModelId;
    },
    enabled: enabled && models.length > 0,
  });
}

interface UseDocumentReferenceForBinaryIdArgs {
  upid: string;
  binaryId: string;
  enabled: boolean;
  includePlainText?: boolean;
}

export function useDocumentReferenceForBinaryId({
  upid,
  binaryId,
  enabled,
  includePlainText = false,
}: UseDocumentReferenceForBinaryIdArgs) {
  return useCtwQuery({
    queryId: QUERY_KEY_DOCUMENT_REFERENCE_FOR_BINARY_ID,
    queryKey: [upid, binaryId, includePlainText],
    queryFn: async ({ telemetry, graphqlClient }) => {
      return await getDocumentReferenceForBinaryId({
        upid,
        binaryId,
        graphqlClient,
        telemetry,
        includePlainText,
        options: FQS_HOTSTORAGE_REQUEST_OPTIONS,
      });
    },
    enabled,
  });
}
interface GetDocumentReferenceForBinaryIdArgs {
  upid: string;
  binaryId: string;
  graphqlClient: GraphQLClient;
  telemetry: ReturnType<typeof useTelemetry>;
  includePlainText?: boolean;
  options?: FqsRequestOptions;
}

export const getDocumentReferenceForBinaryId = async ({
  binaryId,
  graphqlClient,
  telemetry,
  includePlainText = false,
  options,
}: GetDocumentReferenceForBinaryIdArgs) => {
  const [doc] = await getDocumentReferencesForBinaryIds({
    binaryIds: [binaryId],
    graphqlClient,
    telemetry,
    includePlainText,
    options,
  });
  // React Query treats an `undefined` result as an error.
  return doc ?? null;
};

interface GetDocumentReferencesForBinaryIdsArgs {
  binaryIds: Array<string>;
  graphqlClient: GraphQLClient;
  telemetry: ReturnType<typeof useTelemetry>;
  includePlainText?: boolean;
  includeCustodianOrganization?: boolean;
  options?: FqsRequestOptions;
}

/**
 * Batched variant of {@link getDocumentReferenceForBinaryId}. Issues a single
 * aliased `GetProvenances` query covering all binary IDs, then a single
 * aliased `DocumentReference(id:)` point-lookup batch for all referenced
 * DocumentReference IDs. Returns one DocumentModel per input binary ID (in
 * input order), dropping entries with no provenance or no renderable
 * DocumentReference match.
 *
 * Pass `includeCustodianOrganization` only where a document row displays the resolved provider
 * name (`DocumentModel.providerDisplay`); every other caller leaves it `false` so it isn't paying
 * for a reference resolution it never renders.
 */
export const getDocumentReferencesForBinaryIds = async ({
  binaryIds,
  graphqlClient,
  telemetry,
  includePlainText = false,
  includeCustodianOrganization = false,
  options,
}: GetDocumentReferencesForBinaryIdsArgs): Promise<Array<DocumentModel>> => {
  if (binaryIds.length === 0) {
    return [];
  }

  // Sort inputs so the resulting GraphQL query strings + filter payloads are
  // content-addressable — identical inputs in any order produce identical
  // `fqsRequest` dedupe keys, letting concurrent duplicate calls (Strict Mode
  // double-mounts, multiple drawer renders) collapse onto a single network
  // request at each hop.
  const sortedBinaryIds = [...binaryIds].sort();

  const provenances = await searchProvenancesFQS(
    telemetry,
    graphqlClient,
    'Binary',
    sortedBinaryIds,
  );

  // Preserve the per-binary telemetry from the single-id helper.
  for (const binaryId of sortedBinaryIds) {
    const hasProvenance = provenances.some((provenance) =>
      some(provenance.target, (target) => target.reference?.includes(binaryId)),
    );
    if (!hasProvenance) {
      telemetry.trackError({
        message: 'No provenance found for binary',
        error: new Error('Unable to find DocumentReference for binary'),
        context: { binaryId },
      });
    }
  }

  const documentReferenceIds = uniq(
    compact(
      provenances.flatMap((provenance) =>
        provenance.target.map((target) =>
          target.reference?.includes('DocumentReference') ? target.reference?.split('/')[1] : null,
        ),
      ),
    ),
  ).sort();

  // Uses the aliased `DocumentReference(id:)` point-lookup path. The
  // `DocumentReferenceConnection(filter: { ids: anymatch })` alternative
  // triggers a full-collection scan on large patients (~15s+ for 24 IDs)
  // whereas the aliased point-lookup returns in <200ms.
  const documentReferences = await getDocumentReferencesByIdViaAliases(
    telemetry,
    graphqlClient,
    documentReferenceIds,
    options,
    includeCustodianOrganization,
  );

  const renderable = filterToRenderableDocuments({
    documents: documentReferences,
    includePlainText,
  });

  // Maintain input-ordering by binary IDs and dedupe
  const resultsByBinaryId = new Map<string, DocumentModel>();
  for (const documentReference of renderable) {
    const binaryId = documentReference.binaryId;
    if (binaryId && !resultsByBinaryId.has(binaryId)) {
      resultsByBinaryId.set(binaryId, documentReference);
    }
  }
  return compact(uniq(binaryIds).map((binaryId) => resultsByBinaryId.get(binaryId)));
};

interface UseDocumentReferencesForBinaryIdsArgs {
  upid: string;
  binaryIds: Array<string>;
  enabled: boolean;
  includePlainText?: boolean;
  includeCustodianOrganization?: boolean;
}

export function useDocumentReferencesForBinaryIds({
  upid,
  binaryIds,
  enabled,
  includePlainText = false,
  includeCustodianOrganization = false,
}: UseDocumentReferencesForBinaryIdsArgs) {
  // Sort the key component so React Query's structural hash is stable regardless
  // of input order (e.g., re-renders that produce a new array instance from a getter).
  // Pass the same `sortedBinaryIds` to the queryFn so the cached result matches
  // the cache key's canonical order — otherwise two callers with the same sorted
  // IDs but different original orders could share a cache entry whose output
  // ordering reflects only the first caller's input.
  const sortedBinaryIds = [...binaryIds].sort();
  return useCtwQuery({
    queryId: QUERY_KEY_DOCUMENT_REFERENCES_FOR_BINARY_IDS,
    queryKey: [upid, sortedBinaryIds, includePlainText, includeCustodianOrganization],
    queryFn: async ({ telemetry, graphqlClient }) =>
      getDocumentReferencesForBinaryIds({
        binaryIds: sortedBinaryIds,
        graphqlClient,
        telemetry,
        includePlainText,
        includeCustodianOrganization,
        options: FQS_HOTSTORAGE_REQUEST_OPTIONS,
      }),
    enabled: enabled && binaryIds.length > 0,
  });
}

async function blobToBase64(blob: Blob): Promise<string> {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.readAsDataURL(blob);

    reader.onload = () => {
      const base64String = reader.result?.toString().split(',')[1] ?? ''; // Remove data: prefix
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const text = new TextDecoder().decode(bytes);
      resolve(text);
    };

    reader.onerror = (error) => {
      reject(error);
    };
  });
}

export type BinaryData = {
  binary: Binary & {
    blob: Blob;
    /**
     * Set when the document is a CDA that exceeds {@link maximumRenderableCdaDocumentSizeInBytes}
     * and was refused before decoding. `data` is left undefined in that case; the raw `blob` is
     * still populated so "Download XML" keeps working.
     */
    exceedsMaximumRenderableSize?: boolean;
  };
};

export type FQSBinaryJSON = {
  data: {
    contentType: string;
    id: string;
    meta: Binary['meta'];
    resourceType: 'Binary';
    securityContext: {
      reference: string;
      type: string;
    };
  };
};

export async function getBinaryDocument(
  fetchFromFqs: CTWState['fetchFromFqs'],
  binaryId: string,
): Promise<BinaryData> {
  const { data, headers } = await fetchFromFqs(`Binary/${binaryId}`, {
    method: 'GET',
    headers: HOTSTORAGE_REQUEST_HEADERS,
  });

  const contentType = headers.get('Content-Type') || 'unknown';

  if (data instanceof Blob) {
    // FQS blobs contain the raw decoded bytes, so `data.size` is the decoded size. Refuse
    // over-limit CDA documents before `blobToBase64` reads the whole blob into memory. The blob
    // is preserved so "Download XML" still works; `data` is left undefined to signal the refusal.
    if (contentType.includes('xml') && data.size >= maximumRenderableCdaDocumentSizeInBytes) {
      return {
        binary: {
          blob: data,
          contentType,
          resourceType: 'Binary',
          exceedsMaximumRenderableSize: true,
        },
      };
    }

    // The Smile response is a base64 string but FQS returns a blob.
    // The blob can easily convert to base64 string, but is flaky converting
    // back from base64 blob because we strip off any header information the
    // blob had when converting to base64 decoded string. So we add `blob` to
    // the BinaryData object so that we have it later as-is.

    return {
      binary: {
        blob: data,
        data: await blobToBase64(data),
        contentType,
        resourceType: 'Binary',
      },
    };
  }

  // Support for the `text/plain` mimetype
  if (typeof data === 'string') {
    const plainTextBlob = new Blob([data], { type: 'text/plain' });

    // A base64 string decodes to ~3/4 of its length; refuse over-limit CDA documents before
    // `atob` allocates the decoded string. The blob is preserved so "Download XML" still works.
    if (
      contentType.includes('xml') &&
      estimatedDecodedSizeOfBase64InBytes(data.length) >= maximumRenderableCdaDocumentSizeInBytes
    ) {
      return {
        binary: {
          blob: plainTextBlob,
          contentType,
          resourceType: 'Binary',
          exceedsMaximumRenderableSize: true,
        },
      };
    }

    try {
      // try → handle base64 strings
      return {
        binary: {
          blob: plainTextBlob,
          data: atob(data),
          contentType,
          resourceType: 'Binary',
        },
      };
    } catch {
      // catch → handle non-base64 plain text
      return {
        binary: {
          blob: plainTextBlob,
          data,
          contentType,
          resourceType: 'Binary',
        },
      };
    }
  }

  throw new Error('Response from FQS was not a Blob base64 string, or plain text string');
}

export const useGetBinaryDocument = (binaryId: string, enabled = true) =>
  useCtwQuery({
    queryId: QUERY_KEY_BINARY,
    queryKey: [binaryId],
    queryFn: async ({ fetchFromFqs }) => getBinaryDocument(fetchFromFqs, binaryId),
    enabled,
  });

export const useGetPatientFromBinaryId = (binaryId: string, enabled: boolean) => {
  const crossBuilderLookup = useFeatureFlag('ctw-cross-builder-upid-lookup');
  return useCtwQuery({
    queryId: QUERY_KEY_CCDA_PATIENT,
    queryKey: [binaryId],
    queryFn: async ({
      ctwFetch,
      requestContext,
      fetchFromFqs,
    }): Promise<PatientModel | undefined> => {
      const binaryToUpidRequest = (await fetchFromFqs(`rest/Binary/${binaryId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/fhir+json',
          ...HOTSTORAGE_REQUEST_HEADERS,
        },
      })) as FQSBinaryJSON;

      const upid = find(binaryToUpidRequest.data.meta?.extension, {
        url: SYSTEM_ZUS_UNIVERSAL_ID,
      })?.valueString;

      if (upid) {
        return getFirstPartyPatientByIdentifier(
          ctwFetch,
          requestContext,
          upid,
          SYSTEM_ZUS_UNIVERSAL_ID,
          crossBuilderLookup,
        );
      }

      return undefined;
    },
    enabled,
  });
};
